import React, { CSSProperties, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useSuccessAlert } from '../../../../App.hooks';
import DialogBase from '../../../../base/DialogBase/DialogBase';
import {
    ContentRequestTypeEnum, ContentVersion, EditContentRequestInput,
    useGetClientsQuery, useGetLibrariesQuery, useEditContentRequestMutation,
} from '../../../../generated/graphql';
import { gql, useQuery } from '@apollo/client';
import RequestTagSelector from '../../../main/RequestTagSelector/RequestTagSelector';

const GET_REQUEST_TAG_IDS = gql`
    query GetRequestTagIds($id: String) {
        ContentVersion(id: $id) {
            tagIds
        }
    }
`;

const CATEGORIES = [
    "Generic Annotations", "Detail Item Tags", "Generic Model Tags", "Property Tags",
    "Property Line Segment Tags", "Title Blocks", "Callout Heads", "Elevation Marks",
    "Grid Heads", "Level Heads", "View Reference", "Section Marks",
    "Spot Elevation Symbols", "View Titles", "Detail Items", "Generic Models",
    "Span Direction Symbol", "Assembly Tags", "Structural Column Tags",
    "Structural Framing Tags", "Structural Connection Tags", "Floor Tags",
    "Structural Foundation Tags", "Stair Tags", "Structural Truss Tags", "Wall Tags",
    "Structural Columns", "Structural Connections", "Doors", "Structural Foundations",
    "Structural Framing", "Structural Stiffeners", "Windows", "Pipe Types",
    "Ceiling Tags", "Door Tags", "Mechanical Equipment Tags", "Pipe Tags",
    "Pipe Accessory Tags", "Pipe Fitting Tags", "Plumbing Fixture Tags", "Room Tags",
    "Space Tags", "Specialty Equipment Tags", "Flex Pipe Round", "Pipe Accessories",
    "Pipe Fittings", "Mechanical Equipment", "Cable Tray Tags", "Conduit Tags",
    "Electrical Fixture Tags", "Electrical Equipment Tags", "Lighting Device Tags",
    "Lighting Fixture Tags", "Wire Tags", "Conduit Fittings", "Cable Tray Fittings",
    "Lighting Devices", "Lighting Fixtures", "Electrical Equipment", "Electrical Fixtures",
    "Fire Alarm Devices", "Fire Alarm Device Tags", "Sprinkler Tags", "Plumbing Fixtures",
    "Specialty Equipment", "Sprinklers", "Communication Device Tags", "Data Device Tags",
    "Nurse Call Device Tags", "Security Device Tags", "Security Devices",
    "Communication Devices", "Data Devices", "Duct Tags", "Duct Accessory Tags",
    "Duct Fitting Tags", "Duct Insulation Tags", "Duct Lining Tags", "Flex Duct Tags",
    "Zone Tags", "Air Terminal Tags", "Duct Fittings", "Air Terminals",
    "Duct Accessories", "Round Duct", "Rectangular Duct", "Floor", "Basic Wall",
    "Foundation Slab", "Compound Ceiling", "Curtain System", "Cable Tray with Fittings",
    "Conduit with Fittings", "Conduit without Fittings", "Curtain Wall", "Basic Roof",
    "Flex Duct Round", "Roof Soffit", "Pad", "Slab Edge", "Sloped Glazing",
    "Flex Duct Rectangular",
];

const STEPS = ['General', 'Client', 'Comments'];

type EditRequestDialogProps = {
    onClose: () => void;
    visible: boolean;
    style?: CSSProperties;
    children?: ReactNode;
    request: ContentVersion | undefined;
};

type FieldProps = {
    label: string;
    error?: string;
    required?: boolean;
    children: React.ReactNode;
};

// Keep this component outside EditRequestDialog. Defining it inside the dialog
// gives React a new component type on every state update, which remounts inputs
// and causes RequestTagSelector's validity effect to repeatedly reset this step.
const Field = ({ label, error, required, children }: FieldProps) => (
    <div className="flex flex-col gap-1">
        <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
            {label}{required && <span style={{ color: 'var(--error, #dc2626)' }}> *</span>}
        </label>
        {children}
        {error && <span className="text-xs" style={{ color: 'var(--error, #dc2626)' }}>{error}</span>}
    </div>
);

const EditRequestDialog = ({ visible, onClose, request, style }: EditRequestDialogProps) => {
    const [step, setStep] = useState(0);
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [clientId, setClientId] = useState('');
    const [libraryId, setLibraryId] = useState('');
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [tagsValid, setTagsValid] = useState(false);
    const [missingTagTypes, setMissingTagTypes] = useState<string[]>([]);
    const [comment, setComment] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const initializedRequestId = useRef<string | null>(null);
    const initializedTagRequestId = useRef<string | null>(null);

    const { data: clientsData } = useGetClientsQuery({ variables: { contains: '' } });
    const { data: libraryData, refetch: refetchLibraries } = useGetLibrariesQuery();
    const [editRequest, { loading }] = useEditContentRequestMutation();
    const { data: requestTagsData, loading: requestTagsLoading, error: requestTagsError } = useQuery(GET_REQUEST_TAG_IDS, {
        variables: { id: request?.id },
        skip: !visible || !request?.id,
        fetchPolicy: 'network-only',
    });
    const setSuccessAlert = useSuccessAlert();

    const clients = clientsData?.ClientList?.nodes ?? [];
    const libraries = libraryData?.LibraryList?.nodes ?? [];
    const categoryOptions = category && !CATEGORIES.includes(category)
        ? [category, ...CATEGORIES]
        : CATEGORIES;
    const isExistingContentRequest = request?.requestType === ContentRequestTypeEnum.EditContent;

    const handleClientChange = (nextClientId: string) => {
        setClientId(nextClientId);
        setLibraryId('');
        setErrors(previous => ({ ...previous, clientId: '', libraryId: '' }));
        if (nextClientId) {
            refetchLibraries({ clientId: nextClientId, contains: '' });
        }
    };

    const handleTagChange = useCallback((ids: string[]) => {
        setTagIds(ids);
        setErrors(previous => ({ ...previous, tagIds: '' }));
    }, []);

    const handleTagValidityChange = useCallback((valid: boolean, missing: string[]) => {
        setTagsValid(valid);
        setMissingTagTypes(previous =>
            previous.length === missing.length && previous.every((type, index) => type === missing[index])
                ? previous
                : missing
        );
    }, []);

    // Prefill the form from the request being edited when the dialog opens.
    useEffect(() => {
        if (!visible) {
            initializedRequestId.current = null;
            initializedTagRequestId.current = null;
            return;
        }
        if (!request?.id || initializedRequestId.current === request.id) return;
        initializedRequestId.current = request.id;
        setStep(0);
        setErrors({});
        setName(request.name ?? '');
        setCategory(request.category ?? '');
        setFile(null);
        setClientId(request.clientId ?? '');
        setLibraryId(request.libraryId ?? '');
        setComment('');
        if (request.clientId) {
            refetchLibraries({ clientId: request.clientId, contains: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, request, refetchLibraries]);

    useEffect(() => {
        if (
            visible
            && request?.id
            && initializedTagRequestId.current !== request.id
            && requestTagsData?.ContentVersion
        ) {
            initializedTagRequestId.current = request.id;
            setTagIds((requestTagsData.ContentVersion.tagIds ?? []).filter((id: string | null): id is string => !!id));
        }
    }, [visible, request?.id, requestTagsData]);

    const validateStep = useCallback((s: number): Record<string, string> => {
        const e: Record<string, string> = {};
        if (s === 0) {
            if (!name.trim()) e.name = 'Name is required.';
            if (!category) e.category = 'Please select a category.';
        } else if (s === 1) {
            if (!clientId) e.clientId = 'Please select a client.';
            if (!libraryId) e.libraryId = 'Please select a library.';
            if (requestTagsLoading || requestTagsError) e.tagIds = requestTagsError
                ? 'Unable to load the request tags.'
                : 'Request tags are still loading.';
            else if (!tagsValid) e.tagIds = missingTagTypes.length
                ? `Select at least one tag for: ${missingTagTypes.join(', ')}.`
                : 'Tag requirements could not be validated.';
        } else if (s === 2) {
            if (!comment.trim()) e.comment = 'A comment is required.';
        }
        return e;
    }, [name, category, clientId, libraryId, requestTagsLoading, requestTagsError, tagsValid, missingTagTypes, comment]);

    const handleNext = () => {
        const e = validateStep(step);
        if (Object.keys(e).length > 0) { setErrors(e); return; }
        setErrors({});
        if (step < STEPS.length - 1) { setStep(s => s + 1); return; }
        // Submit
        const req = {
            name, category,
            clientId,
            libraryId,
            tagIds,
        } as EditContentRequestInput & { clientId: string; libraryId: string; tagIds: string[] };
        (async () => {
            if (!request?.id) throw new Error('The request could not be identified.');
            await editRequest({ variables: { id: request.id, request: req, file, message: comment } });
        })().then(() => { setSuccessAlert('Request Updated!'); resetForm(); onClose(); });
    };

    const resetForm = () => {
        initializedRequestId.current = null;
        initializedTagRequestId.current = null;
        setStep(0); setName(''); setCategory(''); setFile(null);
        setClientId(''); setLibraryId(''); setTagIds([]); setTagsValid(false); setMissingTagTypes([]);
        setComment(''); setErrors({});
    };

    const footerActions = (
        <div className="flex justify-between items-center w-full">
            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Step {step + 1} of {STEPS.length}</span>
            <div className="flex gap-2">
                {step > 0 && (
                    <button className="btn btn-secondary" onClick={() => { setErrors({}); setStep(s => s - 1); }}>Previous</button>
                )}
                <button className="btn btn-primary" onClick={handleNext} disabled={loading}>
                    {step === STEPS.length - 1 ? 'Save Changes' : 'Next'}
                </button>
            </div>
        </div>
    );

    return (
        <DialogBase
            title="Edit Request"
            type="Custom"
            visible={visible}
            onClose={() => { resetForm(); onClose(); }}
            isLoading={loading}
            style={style}
            width="540px"
            actions={footerActions}
        >
            {/* Step indicator */}
            <div className="flex items-center mb-6">
                {STEPS.map((label, i) => (
                    <React.Fragment key={label}>
                        <div className="flex flex-col items-center gap-1">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold"
                                style={{
                                    background: i <= step ? 'var(--accent)' : 'var(--surface-100)',
                                    color: i <= step ? 'white' : 'var(--text-tertiary)',
                                }}>
                                {i + 1}
                            </div>
                            <span className="text-xs whitespace-nowrap" style={{ color: i === step ? 'var(--accent)' : 'var(--text-tertiary)' }}>{label}</span>
                        </div>
                        {i < STEPS.length - 1 && (
                            <div className="flex-1 h-px mx-1 mb-4" style={{ background: i < step ? 'var(--accent)' : 'var(--border-default)' }} />
                        )}
                    </React.Fragment>
                ))}
            </div>

            <div className="flex flex-col gap-4">
                {step === 0 && <>
                    <Field label="Name" error={errors.name} required>
                        <input className="input-base" value={name} onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: '' })); }} placeholder="Request name" />
                    </Field>
                    <Field label="Category" error={errors.category} required>
                        <select className="input-base" value={category} onChange={e => { setCategory(e.target.value); setErrors(p => ({ ...p, category: '' })); }}>
                            <option value="">— Select a category —</option>
                            {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Field>
                    <Field label="File" error={errors.file}>
                        <input type="file" className="input-base cursor-pointer" style={{ paddingTop: '0.375rem' }}
                            onChange={e => { setFile(e.target.files?.[0] ?? null); setErrors(p => ({ ...p, file: '' })); }} />
                        {!file && request?.attachment?.name && (
                            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                Current file: {request.attachment.name}{request.attachment.extension ?? ''} — leave empty to keep it.
                            </span>
                        )}
                    </Field>
                </>}

                {step === 1 && <>
                    <Field label="Client" error={errors.clientId} required>
                        <select
                            className="input-base"
                            value={clientId}
                            disabled={isExistingContentRequest}
                            onChange={event => handleClientChange(event.target.value)}
                        >
                            <option value="">— Select a client —</option>
                            {clients.map(c => <option key={c?.id} value={c?.id ?? ''}>{c?.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Library" error={errors.libraryId} required>
                        <select
                            className="input-base"
                            value={libraryId}
                            disabled={isExistingContentRequest || !clientId}
                            onChange={event => {
                                setLibraryId(event.target.value);
                                setErrors(previous => ({ ...previous, libraryId: '' }));
                            }}
                        >
                            <option value="">— Select a library —</option>
                            {libraries.map(l => <option key={l?.id} value={l?.id ?? ''}>{l?.name}</option>)}
                        </select>
                    </Field>
                    {isExistingContentRequest && (
                        <p className="text-xs -mt-2" style={{ color: 'var(--text-tertiary)' }}>
                            Client and Library are fixed because this request edits existing content.
                        </p>
                    )}
                    <Field label="Tags" error={errors.tagIds} required>
                        <RequestTagSelector
                            value={tagIds}
                            onChange={handleTagChange}
                            onValidityChange={handleTagValidityChange}
                        />
                    </Field>
                </>}

                {step === 2 && (
                    <Field label="Comment" error={errors.comment} required>
                        <textarea className="input-base" rows={4} value={comment} placeholder="Describe what was changed…"
                            onChange={e => { setComment(e.target.value); setErrors(p => ({ ...p, comment: '' })); }} />
                    </Field>
                )}
            </div>
        </DialogBase>
    );
};

export default EditRequestDialog;
